import { useMemo, useState } from 'react';
import { useLanguage } from '@/hooks/useLanguage';
import {
  Bar,
  BarChart,
  Brush,
  CartesianGrid,
  Cell,
  Line,
  LineChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import type { EnergyPeriod, Granularity, TelemetryRow } from '@/types';
import { Card, CardHeader } from '@/components/ui/Card';
import { HourlyLogPdfCard } from '@/components/dashboard/HourlyLogPdfCard';
import { ActivePowerBarChart } from '@/components/charts/ActivePowerBarChart';
import { TodEnergyBarChart } from '@/components/charts/TodEnergyBarChart';
import { SegmentedToggle } from '@/components/ui/SegmentedToggle';
import {
  costRupees, energyDelivered, loadFactor,
  max, mean, min, percentile, powerFactor, safe, isValidPfReading,
  fifteenMinDemandBlocks, maxDemandKvaOf, instantVA,
} from '@/lib/calculations';
import {
  bucketize, costBreakdown, energyByTodPeriod,
  energyWindowFor, energyPeriodLabel,
  energyByTodBuckets,
  evaluatePenalty, windowFor, windowLabel,
} from '@/lib/aggregations';
import { formatKwh, formatNumber, formatPF, formatRupees, formatPct } from '@/lib/formatters';
import {
  PF_PENALTY_THRESHOLD, PF_REBATE_THRESHOLD,
  type ToDPeriod, type PfAdjustmentRow,
} from '@/config/constants';
import { useTariff } from '@/contexts/TenantContext';
import { useAuth } from '@/auth/AuthContext';
import { Gift, ShieldAlert, Activity } from 'lucide-react';
import { cn } from '@/lib/cn';
import { getPFHistogram } from '@/lib/pfStats';
import { format } from 'date-fns';

interface PowerAnalysisProps {
  meterRows: TelemetryRow[];
}

type RangeMode = Granularity | 'custom';

interface CustomRange {
  from: string;
  to: string;
}

const GRAN_OPTS: { value: RangeMode; label: string }[] = [
  { value: 'hourly',  label: 'Today'   },
  { value: 'daily',   label: 'Daily'   },
  { value: 'monthly', label: 'Monthly' },
];

type UtilWindow = 'today' | 'thisWeek' | 'thisMonth' | 'lastMonth';

const UTIL_WIN_OPTS: { value: UtilWindow; label: string }[] = [
  { value: 'today',     label: 'Today'      },
  { value: 'thisWeek',  label: 'This Week'  },
  { value: 'thisMonth', label: 'This Month' },
  { value: 'lastMonth', label: 'Last Month' },
];

function utilWindowRows(rows: TelemetryRow[], w: UtilWindow): TelemetryRow[] {
  if (!rows.length) return [];
  const anchor = rows[rows.length - 1].timestamp;
  const y = anchor.getFullYear(), mo = anchor.getMonth(), d = anchor.getDate();
  if (w === 'lastMonth') {
    const from = new Date(y, mo - 1, 1, 0, 0, 0, 0).getTime();
    const to   = new Date(y, mo,     1, 0, 0, 0, 0).getTime() - 1;
    return rows.filter(r => { const ts = r.timestamp.getTime(); return ts >= from && ts <= to; });
  }
  const from =
    w === 'today'     ? new Date(y, mo, d, 0, 0, 0, 0).getTime()
    : w === 'thisWeek'  ? new Date(y, mo, d - anchor.getDay() , 0, 0, 0, 0).getTime()
    : new Date(y, mo, 1, 0, 0, 0, 0).getTime();
  return rows.filter(r => r.timestamp.getTime() >= from);
}

function utilGranFor(w: UtilWindow): Granularity {
  return w === 'today' ? 'hourly' : 'daily';
}

function utilWinLabel(w: UtilWindow): string {
  return w === 'today'     ? 'Today'
       : w === 'thisWeek'  ? 'This Week'
       : w === 'thisMonth' ? 'This Month'
       : 'Last Month';
}

/* -------------------------------------------------------------------------- */
/*  niceYAxis — clean, evenly-spaced Y-axis ticks                             */
/* -------------------------------------------------------------------------- */

function niceYAxis(rawMax: number, tickCount = 5): { max: number; ticks: number[] } {
  if (rawMax <= 0) {
    const ticks = Array.from({ length: tickCount }, (_, i) => i * 20);
    return { max: (tickCount - 1) * 20, ticks };
  }
  const rawStep    = rawMax / (tickCount - 1);
  const magnitude  = Math.pow(10, Math.floor(Math.log10(rawStep)));
  const normalized = rawStep / magnitude;
  let niceStep: number;
  if      (normalized <= 1)   niceStep = 1   * magnitude;
  else if (normalized <= 2)   niceStep = 2   * magnitude;
  else if (normalized <= 2.5) niceStep = 2.5 * magnitude;
  else if (normalized <= 5)   niceStep = 5   * magnitude;
  else                         niceStep = 10  * magnitude;
  const niceMax = Math.ceil(rawMax / niceStep) * niceStep;
  const ticks   = Array.from({ length: tickCount }, (_, i) => +(i * (niceMax / (tickCount - 1))).toFixed(6));
  return { max: niceMax, ticks };
}

/* -------------------------------------------------------------------------- */
/*  KwKvaCard — Active Power & Apparent Power (last 24 h)                    */
/* -------------------------------------------------------------------------- */

/**
 * Dynamic symmetric x-axis tick selector.
 *
 * Given an array of data points, picks `targetCount` evenly-spaced labels
 * so ticks are symmetrically distributed across the full width of the chart.
 * Always includes the first and last label so the axis spans edge-to-edge.
 *
 * Rules:
 *   - If data.length <= targetCount  → show every label
 *   - Otherwise compute step = (n-1) / (targetCount-1) and pick indices
 *     [0, step, 2×step, …, n-1] rounded to nearest integer
 *   - Deduplication ensures no label appears twice even with rounding
 *
 * targetCount defaults to 5 which gives good readability on a 320–400 px card.
 * For denser data (15-min blocks = 96 points in a day) this automatically
 * shows e.g. 00:00 · 06:00 · 12:00 · 18:00 · 23:45 — perfectly symmetric.
 */
function symmetricXTicks(data: { label: string }[], targetCount = 5): string[] {
  const n = data.length;
  if (n === 0) return [];
  if (n <= targetCount) return data.map(d => d.label);
  const seen = new Set<string>();
  const ticks: string[] = [];
  for (let i = 0; i < targetCount; i++) {
    const idx   = Math.round(i * (n - 1) / (targetCount - 1));
    const label = data[idx].label;
    if (!seen.has(label)) { seen.add(label); ticks.push(label); }
  }
  return ticks;
}

// Legacy helper kept for KwKvaCard (last-24h line chart uses a different label format)
// const KW_TICK_HOURS_PA = ['06:00', '12:00', '18:00', '00:00'];
function getKwTicksPA(data: { label: string }[]): string[] {
  return symmetricXTicks(data, 5);
}

/**
 * Clamps an array of numbers to their 99.5th-percentile ceiling so that
 * a single spike cannot distort the chart y-axis or calculated statistics.
 * Values above the ceiling are set to the ceiling, never dropped.
 * If fewer than 10 samples exist the data is returned unclamped.
 */
function clampTo995<T extends Record<string, unknown>>(
  rows: T[],
  fields: (keyof T)[],
): T[] {
  if (rows.length < 10) return rows;
  const ceilings = new Map<keyof T, number>();
  for (const field of fields) {
    const vals = rows
      .map(r => r[field] as number)
      .filter(v => Number.isFinite(v) && v > 0);
    if (vals.length >= 10) ceilings.set(field, percentile(vals, 99.5));
  }
  if (ceilings.size === 0) return rows;
  return rows.map(r => {
    let changed = false;
    const copy: Record<string, unknown> = { ...r };
    for (const [field, ceiling] of ceilings) {
      const v = r[field] as number;
      if (Number.isFinite(v) && v > ceiling) { copy[field as string] = ceiling; changed = true; }
    }
    return (changed ? copy : r) as T;
  });
}

function KwKvaCard({ meterRows }: { meterRows: TelemetryRow[] }) {
  const { t } = useLanguage();
  const series = useMemo(() => {
    const cutoff = Date.now() - 24 * 3_600_000;
    const raw = meterRows
      .filter(r => r.timestamp.getTime() >= cutoff)
      .map(r => ({
        label: r.timestamp.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: false }),
        kw:  +(safe(r.wattsTotal) / 1000).toFixed(3),
        // VA = Watts / PF per spec; fall back to vaTotal
        kva: +(instantVA(r) / 1000).toFixed(3),
      }));
    // Clamp spikes to 99.5th-percentile so a single bad reading can't distort the chart
    return clampTo995(raw, ['kw', 'kva']);
  }, [meterRows]);

  const [showKw,  setShowKw]  = useState(true);
  const [showKva, setShowKva] = useState(true);

  const avgKw  = useMemo(() => series.length ? series.reduce((s, r) => s + r.kw,  0) / series.length : 0, [series]);
  const avgKva = useMemo(() => series.length ? series.reduce((s, r) => s + r.kva, 0) / series.length : 0, [series]);

  const yVals = series.flatMap(d => [showKw ? d.kw : null, showKva ? d.kva : null].filter(v => v != null) as number[]);
  const rawMax = yVals.length ? Math.max(...yVals) : 1;
  const { max: yHigh, ticks: yTicks } = niceYAxis(rawMax, 5);
  const yMin = 0;

  const toggleKw  = () => { if (!showKw  || showKva) setShowKw(v  => !v); };
  const toggleKva = () => { if (!showKva || showKw)  setShowKva(v => !v); };

  return (
    <Card>
      <p className="text-[11px] font-bold tracking-wide uppercase text-ink-900 mb-0.5">
        {t("Active Power & Apparent Power")}
      </p>
      <p className="text-xs text-ink-500 mb-3">{t("Last 24 hours · kW (active) and kVA (apparent)")}</p>

      <div className="flex items-center gap-2 mb-3">
        {([
          { key: 'kw',  label: 'kW',  color: '#D97706', active: showKw,  toggle: toggleKw  },
          { key: 'kva', label: 'kVA', color: '#2563EB', active: showKva, toggle: toggleKva },
        ] as const).map(ch => (
          <button
            key={ch.key}
            type="button"
            onClick={ch.toggle}
            className={cn(
              'inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[12px] font-semibold',
              'border transition-all duration-200 select-none active:scale-95',
              ch.active ? 'text-white shadow-sm border-transparent' : 'border-ink-200 text-ink-900 bg-white',
            )}
            style={ch.active ? { backgroundColor: ch.color } : {}}
          >
            <span className="w-1.5 h-1.5 rounded-full" style={{ background: ch.active ? 'white' : ch.color }} />
            {ch.label}
          </button>
        ))}
      </div>

      {series.length ? (
        <>
          <div style={{ width: '100%', height: 220 }}>
            <ResponsiveContainer>
              <LineChart data={series} margin={{ top: 8, right: 8, left: 2, bottom: 0 }}>
                <CartesianGrid stroke="#E2E8F0" vertical={false} />
                <XAxis dataKey="label" tickLine={false} axisLine={false}
                  ticks={getKwTicksPA(series)} interval={0}
                  tick={{ fontSize: 11, fontWeight: 700, fill: '#0F172A' }} />
                <YAxis tickLine={false} axisLine={false} width={46}
                  domain={[yMin, yHigh]}
                  ticks={yTicks}
                  tickFormatter={v => v >= 1000 ? `${(v / 1000).toFixed(1)}k` : Math.round(v).toString()}
                  tick={{ fontSize: 11, fontWeight: 700, fill: '#0F172A' }} />
                <Tooltip
                  cursor={{ stroke: '#94A3B8', strokeWidth: 1, strokeDasharray: '4 3' }}
                  content={({ active, payload, label }) => {
                    if (!active || !payload?.length) return null;
                    return (
                      <div className="rounded-xl bg-white shadow-lg border border-slate-200 px-3 py-2.5 text-xs min-w-[130px]">
                        <div className="font-bold text-slate-900 mb-2">{label}</div>
                        {payload.map(p => (
                          <div key={String(p.dataKey)} className="flex items-center justify-between gap-3 mb-1 last:mb-0">
                            <span className="flex items-center gap-1.5">
                              <span className="w-2 h-2 rounded-full" style={{ background: p.stroke as string }} />
                              <span className="font-semibold text-slate-700">{p.dataKey === 'kw' ? t('Active (kW)') : t('Apparent (kVA)')}</span>
                            </span>
                            <span className="num font-bold text-slate-900">{formatNumber(p.value as number, 2)}</span>
                          </div>
                        ))}
                      </div>
                    );
                  }}
                />
                {showKw  && <Line type="monotoneX" dataKey="kw"  stroke="#D97706" strokeWidth={2.5} dot={false} activeDot={{ r: 3.5, strokeWidth: 0, fill: '#D97706' }} />}
                {showKva && <Line type="monotoneX" dataKey="kva" stroke="#2563EB" strokeWidth={2.5} dot={false} activeDot={{ r: 3.5, strokeWidth: 0 }} />}
              </LineChart>
            </ResponsiveContainer>
          </div>
          <div className="mt-3 grid grid-cols-2 gap-2">
            <div className="rounded-xl px-3 py-2.5 bg-amber-50 border border-amber-200/60 text-center">
              <div className="text-[10px] font-bold uppercase tracking-wide text-amber-700 mb-0.5">{t("Avg Active Power")}</div>
              <div className="num text-sm font-bold text-amber-700">{formatNumber(avgKw, 1)} kW</div>
            </div>
            <div className="rounded-xl px-3 py-2.5 bg-blue-50 border border-blue-200/60 text-center">
              <div className="text-[10px] font-bold uppercase tracking-wide text-blue-700 mb-0.5">{t("Avg Apparent Power")}</div>
              <div className="num text-sm font-bold text-blue-700">{formatNumber(avgKva, 1)} kVA</div>
            </div>
          </div>
        </>
      ) : (
        <div className="flex items-center justify-center h-36 text-xs font-semibold text-ink-500">
          {t("No data in the last 24 hours")}
        </div>
      )}
    </Card>
  );
}

/* -------------------------------------------------------------------------- */
/*  Top-level                                                                 */
/* -------------------------------------------------------------------------- */

export function PowerAnalysis({ meterRows }: PowerAnalysisProps) {
  const { selectedSheet } = useAuth();
  const tariff = useTariff();
  // WBSEDCL (kWh tariff) → WB-only hourly log PDF with the kVAh/PF clamp fix.
  // TGSPDCL (kVAh tariff) keeps the original shared HourlyLogPdfCard untouched.
  const isWb = tariff.energyUnit !== 'kvah';

  const [siteName, meterLabel] = (() => {
    if (!selectedSheet?.label) return ['Site', 'Meter'];
    const parts = selectedSheet.label.split(' · ');
    return [parts[0] ?? selectedSheet.label, parts[1] ?? 'Main'];
  })();

  return (
    <section className="space-y-3">
      <KwKvaCard             meterRows={meterRows} />
      <EnergyByTodCombinedCard meterRows={meterRows} />
      {/* WB Hourly Power Log PDF card — temporarily hidden
      {isWb
        ? <HourlyLogPdfCardWB meterRows={meterRows} siteName={siteName} meterName={meterLabel} />
        : <HourlyLogPdfCard   meterRows={meterRows} siteName={siteName} meterName={meterLabel} />}
      */}
      {!isWb && <HourlyLogPdfCard meterRows={meterRows} siteName={siteName} meterName={meterLabel} />}
      {/* Power Factor Monitoring — shown for all states */}
      <PowerFactorCard meterRows={meterRows} />
      <ContractDemandCard    meterRows={meterRows} />
      {/* Load Factor Utilisation — shown for all states */}
      <LoadFactorCard meterRows={meterRows} />
    </section>
  );
}

/* -------------------------------------------------------------------------- */
/*  EmptyChart — proper skeleton chart with axes, grid, no data              */
/* -------------------------------------------------------------------------- */

const EMPTY_SKELETON_DATA = [
  { label: '06:00', v: null },
  { label: '12:00', v: null },
  { label: '18:00', v: null },
  { label: '00:00', v: null },
];

function EmptyChart({ height = 220 }: { height?: number }) {
  return (
    <div style={{ width: '100%', height }}>
      <ResponsiveContainer>
        <LineChart data={EMPTY_SKELETON_DATA} margin={{ top: 8, right: 8, left: 8, bottom: 0 }}>
          <CartesianGrid stroke="#F1F5F9" vertical={false} />
          <XAxis dataKey="label" tickLine={false} axisLine={false}
            tick={{ fontSize: 10, fontWeight: 700, fill: '#0F172A' }} />
          <YAxis tickLine={false} axisLine={false} width={36}
            domain={[0, 100]} ticks={[0, 25, 50, 75, 100]}
            tickFormatter={v => `${v}`}
            tick={{ fontSize: 10, fontWeight: 700, fill: '#0F172A' }} />
          <Line type="monotone" dataKey="v" stroke="transparent" dot={false} />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}


function EnergyByTodCombinedCard({ meterRows }: { meterRows: TelemetryRow[] }) {
  const { t } = useLanguage();
  const tariff = useTariff();
  const isKvah = tariff.energyUnit === 'kvah';
  const energyUnitLabel = isKvah ? 'kVAh' : 'kWh';

  const [win, setWin] = useState<UtilWindow>('today');

  const windowRows = useMemo(() => utilWindowRows(meterRows, win), [meterRows, win]);
  const bucketGran = utilGranFor(win);
  const buckets    = useMemo(() => bucketize(windowRows, bucketGran), [windowRows, bucketGran]);
  const todBuckets = useMemo(() => energyByTodBuckets(windowRows, bucketGran, isKvah), [windowRows, bucketGran, isKvah]);

  // TGSPDCL: For kVAh tariffs the per-bucket energy comes from todBuckets
  // (energyByTodBuckets with useKvah=true → kvahTrapezoid in aggregations.ts),
  // NOT from buckets.kwh (which always uses energyDelivered → active kWh).
  // kvahTrapezoid mirrors billing.ts kvahDelivered(): trapezoidal integration
  // of kvaTotal, so chart bars and MIN/AVG/PEAK tiles match the billing engine.
  const energyVals = isKvah
    ? todBuckets
        .map((b) => b.normal + b.peak + b.offPeak)
        .filter((v) => Number.isFinite(v) && v > 0)
    : buckets.map((b) => b.kwh).filter((v) => Number.isFinite(v) && v > 0);
  const summary = { min: min(energyVals), avg: mean(energyVals), max: max(energyVals) };

  const chartMode: 'kw' | 'kwh' = 'kwh';
  const chartTitle = bucketGran === 'hourly'
    ? `${energyUnitLabel} · ${t('energy per hour')}`
    : `${energyUnitLabel} · ${t('energy per day')}`;

  return (
    <Card>
      <p className="text-[11px] font-bold tracking-wide uppercase text-ink-900 mb-0.5">{t("Energy by Time-of-Day")}</p>
      <CardHeader
        title={chartTitle}
        description={`${t("Across")} ${t(utilWinLabel(win))}`}
      />
      <div className="mt-2 mb-3">
        <SegmentedToggle size="sm" value={win} onChange={v => setWin(v as UtilWindow)} options={UTIL_WIN_OPTS} ariaLabel="Active power period" />
      </div>
      <>
        {windowRows.length ? (
          <>
            <ActivePowerBarChart buckets={buckets} todBuckets={todBuckets} height={220} mode={chartMode} unit={energyUnitLabel} />
            <div className="mt-2 grid grid-cols-3 gap-2 text-center">
              <MiniStat label={`${t("Min")} ${energyUnitLabel}`} value={`${formatNumber(summary.min)} ${energyUnitLabel}`} />
              <MiniStat label={`${t("Avg")} ${energyUnitLabel}`} value={`${formatNumber(summary.avg)} ${energyUnitLabel}`} accent="brand" />
              <MiniStat label={`${t("Peak")} ${energyUnitLabel}`} value={`${formatNumber(summary.max)} ${energyUnitLabel}`} />
            </div>
          </>
        ) : (
          <EmptyChart height={220} />
        )}
      </>
    </Card>
  );
}

/* -------------------------------------------------------------------------- */
/*  2) Power factor                                                           */
/* -------------------------------------------------------------------------- */

/** Detailed per-0.01-band PF histogram for This Week / This Month */
function PFDetailedHistogram({ rows }: { rows: TelemetryRow[] }) {
  const bins = useMemo(() => {
    const edges = [0.80,0.81,0.82,0.83,0.84,0.85,0.86,0.87,0.88,0.89,
                   0.90,0.91,0.92,0.93,0.94,0.95,0.96,0.97,0.98,0.99,1.00];
    const counts = new Array(edges.length - 1).fill(0);
    for (const r of rows) {
      const pf = powerFactor(r);
      if (!(pf > 0)) continue;
      for (let i = 0; i < edges.length - 1; i++) {
        if (pf >= edges[i] && pf < edges[i + 1]) { counts[i]++; break; }
      }
      if (pf >= 1.00) counts[counts.length - 1]++;
    }
    const total = counts.reduce((s, c) => s + c, 0) || 1;
    return edges.slice(0, -1).map((lo, i) => ({
      label: `${lo.toFixed(2)}`,
      pct:   Number(((counts[i] / total) * 100).toFixed(1)),
      fill:  lo < 0.85 ? '#EF4444' : lo < 0.90 ? '#F59E0B' : lo < 0.95 ? '#60A5FA' : '#10B981',
      period: lo < 0.85 ? 'Critical' : lo < 0.90 ? 'Warning' : lo < 0.95 ? 'Normal' : 'Rebate',
    }));
  }, [rows]);

  if (!bins.some(b => b.pct > 0)) return null;

  return (
    <div className="mt-3">
      <p className="text-[11px] font-bold tracking-wide uppercase text-ink-900 mb-2">
        PF distribution · % time per 0.01 band
      </p>
      <div style={{ width: '100%', height: 200 }}>
        <ResponsiveContainer>
          <BarChart data={bins} margin={{ top: 4, right: 4, left: 8, bottom: 0 }} barCategoryGap="10%">
            <CartesianGrid stroke="#E2E8F0" vertical={false} />
            <XAxis dataKey="label" tickLine={false} axisLine={false}
              interval={4} tick={{ fontSize: 9, fontWeight: 700, fill: '#0F172A' }} />
            <YAxis tickLine={false} axisLine={false} width={36}
              tick={{ fontSize: 9, fontWeight: 700, fill: '#0F172A' }}
              tickFormatter={v => `${v}%`} />
            <ReferenceLine y={0} stroke="#E2E8F0" />
            <Tooltip
              cursor={{ fill: 'rgba(0,0,0,0.04)' }}
              content={({ active, payload }) => {
                if (!active || !payload?.length) return null;
                const d = payload[0]?.payload;
                return (
                  <div className="rounded-xl bg-white shadow-lg border border-slate-200 px-3 py-2 text-xs">
                    <div className="font-bold text-slate-900 mb-1">PF {d.label}–{(Number(d.label)+0.01).toFixed(2)}</div>
                    <div className="flex justify-between gap-4">
                      <span className="font-semibold text-slate-500">{d.period}</span>
                      <span className="num font-bold text-slate-900">{d.pct}% of time</span>
                    </div>
                  </div>
                );
              }}
            />
            <Bar dataKey="pct" radius={[2,2,0,0]} maxBarSize={24}>
              {bins.map((b, i) => <Cell key={i} fill={b.fill} />)}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
      <div className="flex items-center gap-3 mt-2 text-[10px] font-semibold flex-wrap">
        {[['#EF4444','Critical <0.85'],['#F59E0B','Warning 0.85–0.90'],['#60A5FA','Normal 0.90–0.95'],['#10B981','Rebate ≥0.95']].map(([c,l]) => (
          <span key={l} className="flex items-center gap-1">
            <span className="inline-block w-2 h-2 rounded-sm" style={{ background: c }} />{l}
          </span>
        ))}
      </div>
    </div>
  );
}

/**
 * PFTodayRawChart — continuous 5-min interval PF line for "Today" view.
 */
function PFTodayRawChart({ rows, showRefLines = true, height = 220 }: {
  rows:          TelemetryRow[];
  showRefLines?: boolean;
  height?:       number;
}) {
  const { pfPenaltyThreshold: PF_PENALTY_THRESHOLD, pfRebateThreshold: PF_REBATE_THRESHOLD } = useTariff();

  const series = useMemo(() => {
    const raw = rows
      .filter(isValidPfReading)
      .map(r => ({
        label: r.timestamp.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: false }),
        pf: Number(powerFactor(r).toFixed(3)),
      }))
      .filter(d => d.pf > 0);
    // Clamp outlier PF spikes (PF > 1 edge cases from noisy meters)
    return clampTo995(raw, ['pf']);
  }, [rows]);

  // Dynamic symmetric ticks — 5 evenly-spaced labels across the data range
  const xTicks = useMemo(() => symmetricXTicks(series, 5), [series]);

  // Axis bounds
  const pfValues = series.map(d => d.pf);
  const cleanVals = pfValues.filter(v => Number.isFinite(v) && v > 0).sort((a, b) => a - b);
  const axisMin = cleanVals.length ? Math.max(0, Math.floor(Math.min(...cleanVals) * 100) / 100 - 0.02) : 0.90;
  const axisMax = 1.00;
  const span = Math.max(axisMax - axisMin, 0.04);
  const step = span <= 0.04 ? 0.01 : span <= 0.1 ? 0.02 : 0.05;
  const tickMin = Math.floor(axisMin / step) * step;
  const ticks: number[] = [];
  for (let v = tickMin; v <= axisMax + step / 2; v += step) {
    ticks.push(Number(v.toFixed(2)));
  }

  if (!series.length) {
    return <EmptyChart height={height} />;
  }

  return (
    <div style={{ width: '100%', height }}>
      <ResponsiveContainer>
        <LineChart data={series} margin={{ top: 12, right: 4, left: 14, bottom: 0 }}>
          <CartesianGrid stroke="#CBD5E1" vertical={false} />
          <XAxis dataKey="label" tickLine={false} axisLine={false}
            ticks={xTicks} interval={0}
            tick={{ fontSize: 11, fontWeight: 700, fill: '#0F172A' }} />
          <YAxis tickLine={false} axisLine={false} width={44} allowDataOverflow
            domain={[tickMin, axisMax]}
            ticks={ticks}
            tickFormatter={v => v.toFixed(2)}
            tick={{ fontSize: 11, fontWeight: 700, fill: '#0F172A' }} />
          {showRefLines && (
            <ReferenceLine y={PF_REBATE_THRESHOLD} stroke="#6EE7BF" strokeDasharray="3 3" strokeWidth={1}
              label={{ value: `Rebate ${PF_REBATE_THRESHOLD.toFixed(2)}`, position: 'insideBottomRight', fontSize: 10, fontWeight: 700, fill: '#0B8E70' }} />
          )}
          {showRefLines && (
            <ReferenceLine y={PF_PENALTY_THRESHOLD} stroke="#F59E0B" strokeDasharray="4 4" strokeWidth={1.5}
              label={{ value: `Penalty < ${PF_PENALTY_THRESHOLD.toFixed(2)}`, position: 'insideBottomRight', fontSize: 10, fontWeight: 700, fill: '#92400E' }} />
          )}
          <Tooltip
            content={({ active, payload, label }) => {
              if (!active || !payload?.length) return null;
              const pf = payload[0]?.value as number;
              if (!pf) return null;
              const zone =
                pf >= PF_REBATE_THRESHOLD  ? { label: 'Rebate zone',  color: '#0FB18A' }
                : pf >= PF_PENALTY_THRESHOLD ? { label: 'Neutral zone', color: '#F59E0B' }
                :                              { label: 'Penalty zone', color: '#EF4444' };
              return (
                <div className="rounded-xl bg-white shadow-lg border border-slate-200 px-3 py-2 text-xs min-w-[150px]">
                  <div className="font-bold text-slate-900 mb-1">{label}</div>
                  <div className="flex justify-between gap-3">
                    <span className="font-semibold text-slate-700">Power Factor</span>
                    <span className="num font-bold text-slate-900">{pf.toFixed(3)}</span>
                  </div>
                  <div className="flex items-center gap-1.5 mt-1">
                    <span className="w-1.5 h-1.5 rounded-full" style={{ background: zone.color }} />
                    <span className="font-semibold" style={{ color: zone.color }}>{zone.label}</span>
                  </div>
                </div>
              );
            }}
          />
          <Line type="monotone" dataKey="pf" stroke="#0891B2" strokeWidth={2.25}
            dot={false} activeDot={{ r: 4, strokeWidth: 0, fill: '#0891B2' }} />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

function PowerFactorCard({ meterRows }: { meterRows: TelemetryRow[] }) {
  const { t } = useLanguage();
  const [win, setWin] = useState<UtilWindow>('today');
  const { hasPfAdjustment } = useTariff();

  const windowRows = useMemo(() => utilWindowRows(meterRows, win), [meterRows, win]);

  // BUG FIX (Bug #3 — PF averaging method):
  // The previous code computed pfAvg as mean(rows.map(r => powerFactor(r))),
  // i.e. the arithmetic mean of per-row True_PF_Avg readings. True_PF_Avg is
  // the harmonic-inclusive true PF — at this site it reads ~0.981, about 1.5%
  // below the billing-cycle PF of ~0.997 computed as ΔWh/ΔVAh. Displaying the
  // instantaneous mean as the "average PF" for a period caused:
  //   • Incorrect rebate/penalty zone classification (0.981 looks near-threshold)
  //   • Inconsistency with the Billings page which always uses ΔWh/ΔVAh
  //   • Misleading "Today PF below weekly average" comparisons
  //
  // Fix: use register-based PF (ΔWh/ΔVAh from the window's first and last rows),
  // exactly matching buildKpiSummary's registerPF() and the billing engine.
  // The per-row line chart (PFTodayRawChart) intentionally retains True_PF_Avg
  // to show instantaneous waveform quality — that is correct for that context.
  const registerPf = (rs: TelemetryRow[]): number => {
    const sorted = [...rs].sort((a, b) => a.timestamp.getTime() - b.timestamp.getTime());
    if (sorted.length < 2) {
      const valid = sorted.filter(isValidPfReading);
      if (!valid.length) return 0;
      return mean(valid.map((r) => powerFactor(r)).filter((v) => v > 0));
    }
    const first = sorted[0];
    const last  = sorted[sorted.length - 1];
    const dWh   = Math.max(0, safe(last.energyWh)  - safe(first.energyWh));
    const dVAh  = Math.max(0, safe(last.energyVAh) - safe(first.energyVAh));
    if (dVAh > 0) return Math.min(dWh / dVAh, 0.9999);
    const valid = sorted.filter(isValidPfReading);
    if (!valid.length) return 0;
    return mean(valid.map((r) => powerFactor(r)).filter((v) => v > 0));
  };

  const pfAvg   = useMemo(() => registerPf(windowRows),                          [windowRows]);  // eslint-disable-line react-hooks/exhaustive-deps
  const pfToday = useMemo(() => registerPf(utilWindowRows(meterRows, 'today')),   [meterRows]);   // eslint-disable-line react-hooks/exhaustive-deps
  const pfWeekRows = useMemo(() => utilWindowRows(meterRows, 'thisWeek'),         [meterRows]);
  const weekPf     = useMemo(() => registerPf(pfWeekRows),                        [pfWeekRows]);  // eslint-disable-line react-hooks/exhaustive-deps

  const comparison = useMemo(() => {
    if (!hasPfAdjustment) return null;
    if (win === 'today') {
      if (!pfToday || !weekPf) return null;
      const diff = pfToday - weekPf;
      if (Math.abs(diff) < 0.005) return { label: t("Similar to this week's average"), tone: 'neutral' as const };
      return diff > 0
        ? { label: `${t("Today PF")} ${pfToday.toFixed(3)} ${t("is above this week's avg")} (${weekPf.toFixed(3)}) — ${t("good day")}`, tone: 'good' as const }
        : { label: `${t("Today PF")} ${pfToday.toFixed(3)} ${t("is below this week's avg")} (${weekPf.toFixed(3)}) — ${t("below average")}`, tone: 'warn' as const };
    }
    if (!pfToday || !pfAvg) return null;
    const diff = pfToday - pfAvg;
    if (Math.abs(diff) < 0.005) return { label: t("Similar to average"), tone: 'neutral' as const };
    return diff > 0
      ? { label: `${t("Today")} +${diff.toFixed(3)} ${t("above")} ${t(utilWinLabel(win))} ${t("avg")}`, tone: 'good' as const }
      : { label: `${t("Today")} ${diff.toFixed(3)} ${t("below")} ${t(utilWinLabel(win))} ${t("avg")}`, tone: 'warn' as const };
  }, [hasPfAdjustment, win, pfToday, pfAvg, weekPf, t]);

  return (
    <Card>
      <p className="text-[11px] font-bold tracking-wide uppercase text-ink-900 mb-0.5">{t("Power Factor")}</p>
      <CardHeader
        title={
          <span className={cn('num', (win === 'today' && hasPfAdjustment) ? (pfAvg >= 0.95 ? 'text-emerald-600' : pfAvg < 0.80 ? 'text-red-500' : 'text-amber-500') : 'text-ink-900')}>
            {formatPF(pfAvg)}
          </span>
        }
        description={`${t(utilWinLabel(win))} ${t("average")}`}
      />
      <div className="mt-1 mb-2">
        <SegmentedToggle size="sm" value={win} onChange={v => setWin(v as UtilWindow)} options={UTIL_WIN_OPTS} ariaLabel="Power factor window" />
      </div>
      {win === 'today' && comparison && (
        <p className="text-xs font-bold text-slate-900 mb-2">
          {comparison.tone === 'good' ? '↑ ' : comparison.tone === 'warn' ? '↓ ' : '≈ '}
          {comparison.label}
        </p>
      )}
      {windowRows.length === 0 ? (
        <EmptyChart height={200} />
      ) : win === 'today'
        ? <PFTodayRawChart rows={windowRows} showRefLines={hasPfAdjustment} />
        : <PFDetailedHistogram rows={windowRows} />
      }
    </Card>
  );
}

/* -------------------------------------------------------------------------- */
/*  3a) Contract Demand Utilisation                                           */
/* -------------------------------------------------------------------------- */

interface UtilTrendPoint {
  label: string;
  value: number;
}

function maxDemandUtil(rows: TelemetryRow[], cd: number): number {
  return cd > 0 ? (maxDemandKvaOf(rows) / cd) * 100 : 0;
}

function mdBucketKey(d: Date, gran: Granularity): string {
  if (gran === 'hourly') return format(d, 'yyyy-MM-dd HH');
  if (gran === 'daily')  return format(d, 'yyyy-MM-dd');
  return format(d, 'yyyy-MM');
}
function mdBucketLabel(d: Date, gran: Granularity): string {
  if (gran === 'hourly') return `${format(d, 'HH')}:00`;
  if (gran === 'daily')  return format(d, 'd MMM');
  return format(d, 'MMM yy');
}

function perBucketMaxDemand(
  rows: TelemetryRow[], gran: Granularity,
): { ts: number; label: string; maxKva: number }[] {
  const groups = new Map<string, { ts: number; label: string; maxKva: number }>();
  for (const b of fifteenMinDemandBlocks(rows)) {
    const d   = new Date(b.ts);
    const key = mdBucketKey(d, gran);
    const g   = groups.get(key) ?? { ts: b.ts, label: mdBucketLabel(d, gran), maxKva: 0 };
    if (b.kva > g.maxKva) g.maxKva = b.kva;
    groups.set(key, g);
  }
  return [...groups.values()].sort((a, b) => a.ts - b.ts);
}

function getContractDemandTrend(rows: TelemetryRow[], gran: Granularity, cd: number): UtilTrendPoint[] {
  if (!rows.length || cd <= 0) return [];
  return perBucketMaxDemand(rows, gran)
    .map(g => ({ label: g.label, value: +((g.maxKva / cd) * 100).toFixed(1) }));
}

function getMaxDemandRunningMax(rows: TelemetryRow[], gran: Granularity): UtilTrendPoint[] {
  if (!rows.length) return [];
  let run = 0;
  return perBucketMaxDemand(rows, gran)
    .map(g => { if (g.maxKva > run) run = g.maxKva; return { label: g.label, value: +run.toFixed(1) }; });
}

/**
 * Load Factor trend data for bar charts.
 *
 * TODAY (hourly bars) — cumulative formula per spec:
 *   Each hourly bar at hour H shows LF for the period 00:00 → end of hour H.
 *   peakKW_till_H  = max_15min_kVA_till_H × avg_PF_till_H
 *   hoursElapsed   = hours from midnight to end of hour H
 *   LF%            = (kWh_till_H × 100) / (peakKW_till_H × hoursElapsed)
 *
 * WEEK / MONTH (daily bars) — per-day formula:
 *   Each daily bar shows LF for that calendar day (00:00–23:59).
 *   peakKW   = max_15min_kVA_that_day × avg_PF_that_day
 *   LF%      = (kWh_that_day × 100) / (peakKW × 24)
 */
function getLoadFactorTrend(rows: TelemetryRow[], gran: Granularity): UtilTrendPoint[] {
  if (!rows.length) return [];
  const sorted = [...rows].sort((a, b) => a.timestamp.getTime() - b.timestamp.getTime());

  // ── TODAY: cumulative hourly bars ─────────────────────────────────────────
  if (gran === 'hourly') {
    const dayStart = (() => {
      const anchor = sorted[sorted.length - 1].timestamp;
      return new Date(anchor.getFullYear(), anchor.getMonth(), anchor.getDate(), 0, 0, 0, 0).getTime();
    })();
    const todayRows = sorted.filter(r => r.timestamp.getTime() >= dayStart);
    if (todayRows.length < 2) return [];

    // Pre-compute 15-min blocks for today to find peak kVA up to each hour
    const blocks = fifteenMinDemandBlocks(todayRows); // [{ts, kva}]

    const results: UtilTrendPoint[] = [];
    // Walk hour by hour from hour 0 to the last hour that has data
    const lastHour = todayRows[todayRows.length - 1].timestamp.getHours();

    for (let h = 0; h <= lastHour; h++) {
      const endOfHourMs = dayStart + (h + 1) * 3_600_000; // exclusive upper bound
      const sliceRows   = todayRows.filter(r => r.timestamp.getTime() < endOfHourMs);
      if (sliceRows.length < 2) continue;

      // kWh consumed from midnight to end of hour h — trapezoidal
      let kwhTillH = 0;
      for (let i = 1; i < sliceRows.length; i++) {
        const dtH = (sliceRows[i].timestamp.getTime() - sliceRows[i - 1].timestamp.getTime()) / 3_600_000;
        if (dtH <= 0 || dtH > 1) continue;
        kwhTillH += ((safe(sliceRows[i - 1].wattsTotal) + safe(sliceRows[i].wattsTotal)) / 2) * dtH / 1000;
      }

      // Peak kVA from all 15-min blocks up to end of hour h
      const peakKvaTillH = blocks
        .filter(b => b.ts < endOfHourMs)
        .reduce((m, b) => (b.kva > m ? b.kva : m), 0);

      // Average PF from midnight to end of hour h (valid rows only)
      const validPfRows = sliceRows.filter(r => isValidPfReading(r));
      const pfAvgTillH  = validPfRows.length
        ? validPfRows.reduce((s, r) => s + safe(r.pfAvg), 0) / validPfRows.length
        : 0;

      // Peak kW till hour h = peak kVA × avg PF
      const peakKwTillH = peakKvaTillH * Math.min(pfAvgTillH, 1);

      // Hours elapsed = h + 1 (full hours from midnight including current hour)
      const hoursElapsed = h + 1;

      const label = `${String(h).padStart(2, '0')}:00`;

      if (peakKwTillH <= 0 || kwhTillH <= 0) {
        results.push({ label, value: 0 });
        continue;
      }

      // LF% = (kWh_till_H × 100) / (peakKW_till_H × hoursElapsed)
      const value = Math.min(100, (kwhTillH * 100) / (peakKwTillH * hoursElapsed));
      results.push({ label, value: Math.round(value * 10) / 10 });
    }
    return results;
  }

  // ── WEEK / MONTH: per-day bars ────────────────────────────────────────────
  const dayGroups = new Map<string, TelemetryRow[]>();
  for (const r of sorted) {
    const key  = format(r.timestamp, 'yyyy-MM-dd');
    const list = dayGroups.get(key);
    if (list) list.push(r);
    else dayGroups.set(key, [r]);
  }

  return [...dayGroups.entries()]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([key, pts]) => {
      pts.sort((a, b) => a.timestamp.getTime() - b.timestamp.getTime());
      const label = `${Number(key.slice(8, 10))} ${['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'][Number(key.slice(5, 7)) - 1]}`;

      if (pts.length < 2) return { label, value: 0 };

      // kWh for this day — trapezoidal
      let kwhDay = 0;
      for (let i = 1; i < pts.length; i++) {
        const dtH = (pts[i].timestamp.getTime() - pts[i - 1].timestamp.getTime()) / 3_600_000;
        if (dtH <= 0 || dtH > 1) continue;
        kwhDay += ((safe(pts[i - 1].wattsTotal) + safe(pts[i].wattsTotal)) / 2) * dtH / 1000;
      }

      // Peak kVA for this day (max 15-min block)
      const peakKvaDay = fifteenMinDemandBlocks(pts).reduce((m, b) => (b.kva > m ? b.kva : m), 0);

      // Average PF for this day (valid readings only)
      const validPf  = pts.filter(r => isValidPfReading(r));
      const pfAvgDay = validPf.length
        ? validPf.reduce((s, r) => s + safe(r.pfAvg), 0) / validPf.length
        : 0;

      const peakKwDay = peakKvaDay * Math.min(pfAvgDay, 1);

      if (peakKwDay <= 0 || kwhDay <= 0) return { label, value: 0 };

      // LF% = (kWh_day × 100) / (peakKW_day × 24)
      const value = Math.min(100, (kwhDay * 100) / (peakKwDay * 24));
      return { label, value: Math.round(value * 10) / 10 };
    });
}

/* ── Shared single-series line chart ────────────────────────────────────── */

function SingleLineTrendChart({
  data, color, yLabel = '%', refLines = [], lineType = 'monotone', xTicks,
}: {
  data:      UtilTrendPoint[];
  color:     string;
  yLabel?:   string;
  refLines?: { y: number; label: string; stroke: string }[];
  lineType?: 'monotone' | 'stepAfter' | 'step';
  xTicks?:   string[];
}) {
  if (!data.length || data.every(d => d.value === 0)) {
    return <EmptyChart height={200} />;
  }

  const isPct  = yLabel === '%';
  const refMax = refLines.reduce((m, r) => Math.max(m, r.y), 0);

  const dataMax  = Math.max(...data.map(d => d.value), 0);
  const rawMax   = Math.max(dataMax, refMax);
  const { max: yAxisMax, ticks: yAxisTicks } = niceYAxis(rawMax, 5);

  return (
    <div style={{ width: '100%', height: 200 }}>
      <ResponsiveContainer>
        <LineChart data={data} margin={{ top: 18, right: 14, left: 8, bottom: 0 }}>
          <CartesianGrid stroke="#E2E8F0" vertical={false} />
          <XAxis dataKey="label" tickLine={false} axisLine={false}
            {...(xTicks ? { ticks: xTicks, interval: 0 } : { interval: 'preserveStartEnd', minTickGap: 28 })}
            tick={{ fontSize: 11, fontWeight: 700, fill: '#0F172A' }} />
          <YAxis tickLine={false} axisLine={false} width={isPct ? 40 : 44}
            domain={[0, yAxisMax]}
            ticks={yAxisTicks}
            tickFormatter={v => isPct ? `${Math.round(v)}%` : `${v}`}
            tick={{ fontSize: 10, fontWeight: 700, fill: '#0F172A' }} />
          <Tooltip
            cursor={{ stroke: '#CBD5E1', strokeWidth: 1, strokeDasharray: '4 3' }}
            content={({ active, payload, label }) => {
              if (!active || !payload?.length) return null;
              return (
                <div className="rounded-xl bg-white shadow-lg border border-slate-200 px-3 py-2 text-xs min-w-[120px]">
                  <div className="font-bold text-slate-900 mb-1">{label}</div>
                  <div className="num font-bold" style={{ color }}>
                    {(payload[0]?.value as number ?? 0).toFixed(1)}{yLabel}
                  </div>
                </div>
              );
            }}
          />
          {refLines.map(rl => (
            <ReferenceLine key={rl.label} y={rl.y} stroke={rl.stroke} strokeWidth={1.5}
              strokeDasharray="5 3"
              label={{ value: rl.label, position: 'insideTopRight', fontSize: 9, fontWeight: 700, fill: rl.stroke }} />
          ))}
          <Line type={lineType} dataKey="value" stroke={color} strokeWidth={2.25}
            dot={false} activeDot={{ r: 4, strokeWidth: 0, fill: color }} />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

/* ── 3a) Contract Demand Utilisation card ──────────────────────────────── */

// 15-min histogram for today: each bar = avg kVA of one 15-min block (spec §4)
// x-axis: HH:MM of the block start; y-axis: avg kVA (for the kVA view)
// or % utilisation (kVA / CD × 100) for the utilisation view.
function getTodayMdBlocks(rows: TelemetryRow[]): { ts: number; label: string; kva: number }[] {
  if (rows.length < 2) return [];
  const anchor = rows[rows.length - 1].timestamp;
  const dayStart = new Date(
    anchor.getFullYear(), anchor.getMonth(), anchor.getDate(), 0, 0, 0, 0,
  ).getTime();
  const todayRows = rows.filter(r => r.timestamp.getTime() >= dayStart);
  return fifteenMinDemandBlocks(todayRows).map(b => {
    const d = new Date(b.ts);
    const label = `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
    return { ts: b.ts, label, kva: b.kva };
  });
}

// Bar chart for CD utilisation — spec §4 histogram for all windows
function ContractDemandBarChart({
  data, refLines = [], yLabel = '%', xTicks,
}: {
  data: UtilTrendPoint[];
  refLines?: { y: number; label: string; stroke: string }[];
  yLabel?: string;
  xTicks?: string[];
}) {
  if (!data.length || data.every(d => d.value === 0)) return <EmptyChart height={200} />;

  const isPct   = yLabel === '%';
  const dataMax = Math.max(...data.map(d => d.value), 0);
  const refMax  = refLines.reduce((m, r) => Math.max(m, r.y), 0);
  const { max: yAxisMax, ticks: yAxisTicks } = niceYAxis(Math.max(dataMax, refMax), 5);

  const colorFor = isPct
    ? (v: number) => v >= 100 ? '#EF4444' : v >= 85 ? '#F59E0B' : '#60A5FA'
    : (_v: number) => '#8B5CF6';

  return (
    <div style={{ width: '100%', height: 200 }}>
      <ResponsiveContainer>
        <BarChart data={data} margin={{ top: 18, right: 14, left: 8, bottom: 0 }} barCategoryGap="20%">
          <CartesianGrid stroke="#E2E8F0" vertical={false} />
          <XAxis dataKey="label" tickLine={false} axisLine={false}
            {...(xTicks ? { ticks: xTicks, interval: 0 } : { interval: 'preserveStartEnd', minTickGap: 28 })}
            tick={{ fontSize: 11, fontWeight: 700, fill: '#0F172A' }} />
          <YAxis tickLine={false} axisLine={false} width={isPct ? 40 : 48}
            domain={[0, yAxisMax]}
            ticks={yAxisTicks}
            tickFormatter={v => isPct ? `${Math.round(v)}%` : `${v.toFixed(1)}`}
            tick={{ fontSize: 10, fontWeight: 700, fill: '#0F172A' }} />
          <Tooltip
            cursor={{ fill: 'rgba(0,0,0,0.04)' }}
            content={({ active, payload, label }) => {
              if (!active || !payload?.length) return null;
              const v = payload[0]?.value as number ?? 0;
              return (
                <div className="rounded-xl bg-white shadow-lg border border-slate-200 px-3 py-2 text-xs min-w-[130px]">
                  <div className="font-bold text-slate-900 mb-1">{label}</div>
                  <div className="num font-bold" style={{ color: colorFor(v) }}>
                    {isPct ? `${(Math.trunc(v * 100) / 100).toFixed(2)}%` : `${(Math.trunc(v * 100) / 100).toFixed(2)} kVA`}
                  </div>
                </div>
              );
            }}
          />
          {refLines.map(rl => (
            <ReferenceLine key={rl.label} y={rl.y} stroke={rl.stroke} strokeWidth={1.5}
              strokeDasharray="5 3"
              label={{ value: rl.label, position: 'insideTopRight', fontSize: 9, fontWeight: 700, fill: rl.stroke }} />
          ))}
          <Bar dataKey="value" radius={[4, 4, 0, 0]} maxBarSize={32}>
            {data.map((d, i) => <Cell key={i} fill={colorFor(d.value)} />)}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

function ContractDemandCard({ meterRows }: { meterRows: TelemetryRow[] }) {
  const { t } = useLanguage();
  const [win,      setWin]      = useState<UtilWindow>('today');
  const [viewMode, setViewMode] = useState<'utilisation' | 'peakKwh'>('utilisation');
  const windowRows  = useMemo(() => utilWindowRows(meterRows, win), [meterRows, win]);

  const contractKva = useMemo(() => {
    for (const r of meterRows) {
      if (r.loadPct > 0 && r.kvaTotal > 0) return (r.kvaTotal / r.loadPct) * 100;
    }
    return 176;
  }, [meterRows]);

  const mdUtil  = useMemo(() => maxDemandUtil(windowRows, contractKva), [windowRows, contractKva]);

  const todayMd = useMemo(() => maxDemandUtil(utilWindowRows(meterRows, 'today'), contractKva), [meterRows, contractKva]);
  const weekRows = useMemo(() => utilWindowRows(meterRows, 'thisWeek'), [meterRows]);
  const weekMd   = useMemo(() => maxDemandUtil(weekRows, contractKva), [weekRows, contractKva]);

  const comparison = useMemo(() => {
    if (win === 'today') {
      if (!todayMd || !weekMd) return null;
      const diff = todayMd - weekMd;
      if (Math.abs(diff) < 0.5) return { label: t("Similar to this week's average"), tone: 'neutral' as const };
      return diff > 0
        ? { label: `${t("Today is")} +${diff.toFixed(1)}% ${t("above this week's avg")}`, tone: 'good' as const }
        : { label: `${t("Today is")} ${diff.toFixed(1)}% ${t("below this week's avg")}`, tone: 'warn' as const };
    }
    if (!todayMd || !mdUtil) return null;
    const diff = todayMd - mdUtil;
    if (Math.abs(diff) < 0.5) return { label: t("Similar to average"), tone: 'neutral' as const };
    return diff > 0
      ? { label: `${t("Today")} +${diff.toFixed(1)}% ${t("above")} ${t(utilWinLabel(win))} ${t("avg")}`, tone: 'good' as const }
      : { label: `${t("Today")} ${diff.toFixed(1)}% ${t("below")} ${t(utilWinLabel(win))} ${t("avg")}`, tone: 'warn' as const };
  }, [win, todayMd, weekMd, mdUtil, t]);

  // Today: 15-min block histogram per spec §4 (bar chart, one bar per 15-min block)
  const todayMdBlocks = useMemo(() => getTodayMdBlocks(meterRows), [meterRows]);
  // Today utilisation bars: each block's avg kVA as % of CD (spec §3)
  const todayUtilBars = useMemo((): UtilTrendPoint[] =>
    todayMdBlocks.map(b => ({
      label: b.label,
      value: +(contractKva > 0 ? Math.min((b.kva / contractKva) * 100, 200) : 0).toFixed(4),
    })),
  [todayMdBlocks, contractKva]);

  // Week/month: bar chart grouped by day; running-max kVA line for Peak kVA view
  const trendData    = useMemo(() => getContractDemandTrend(windowRows, utilGranFor(win), contractKva), [windowRows, win, contractKva]);
  const mdRunMaxData = useMemo(() => getMaxDemandRunningMax(windowRows, utilGranFor(win)), [windowRows, win]);

  // Fixed x-axis ticks for today's 15-min histogram (HH:MM labels)
  // Anchor at 00:00, 06:00, 12:00, 18:00 + last block
  const todayMdXTicks = useMemo(
    () => symmetricXTicks(todayMdBlocks, 5),
    [todayMdBlocks],
  );

  const currentValue = viewMode === 'utilisation'
    ? formatPct(mdUtil)
    : mdRunMaxData.length ? `${mdRunMaxData[mdRunMaxData.length - 1].value.toFixed(2)} kVA` : '—';

  // Descriptions per spec
  const description = viewMode === 'utilisation'
    ? win === 'today'
      ? `${t("Today")} · (${t("peak kVA today")} / CD)×100`
      : `${t(utilWinLabel(win))} · (${t("peak kVA")} ${t(utilWinLabel(win))} / CD)×100`
    : win === 'today'
      ? `${t("Today")} · ${t("Running maximum of kVA averaged over 15 minutes window")}`
      : `${t(utilWinLabel(win))} · ${t("Running maximum of kVA averaged over 15 minutes window")}`;

  return (
    <Card>
      <p className="text-[11px] font-bold tracking-wide uppercase text-ink-900 mb-0.5">{t("Contract Demand Utilisation")}</p>
      <CardHeader
        title={<span className="num text-ink-900">{currentValue}</span>}
        description={description}
      />

      <div className="mt-2 flex items-center gap-1.5 mb-2">
        {(['utilisation', 'peakKwh'] as const).map(v => (
          <button
            key={v}
            type="button"
            onClick={() => setViewMode(v)}
            className={cn(
              'px-3 py-1.5 rounded-full text-[11px] font-bold border transition-all active:scale-95',
              viewMode === v
                ? 'bg-slate-900 text-white border-slate-900'
                : 'bg-white text-ink-700 border-ink-200',
            )}
          >
            {v === 'utilisation' ? t('Utilisation %') : t('Peak kVA')}
          </button>
        ))}
      </div>

      <div className="mb-2">
        <SegmentedToggle size="sm" value={win} onChange={v => setWin(v as UtilWindow)} options={UTIL_WIN_OPTS} ariaLabel="Contract demand window" />
      </div>

      {win === 'today' && comparison && viewMode === 'utilisation' && (
        <p className="text-xs font-bold text-slate-900 mb-2">
          {comparison.tone === 'good' ? '↑ ' : comparison.tone === 'warn' ? '↓ ' : '≈ '}
          {comparison.label}
        </p>
      )}

      {viewMode === 'utilisation' ? (
        win === 'today' ? (
          /* Today: histogram — one bar per 15-min block, y = % utilisation */
          <ContractDemandBarChart
            data={todayUtilBars}
            refLines={[{ y: 100, label: t('CD 100%'), stroke: '#EF4444' }]}
            xTicks={todayMdXTicks}
          />
        ) : (
          /* Week/month: one bar per day */
          <ContractDemandBarChart
            data={trendData}
            refLines={[{ y: 100, label: t('Contract'), stroke: '#EF4444' }]}
            xTicks={symmetricXTicks(trendData, 5)}
          />
        )
      ) : (
        /* Peak kVA — running-max line for all windows */
        <SingleLineTrendChart
          data={mdRunMaxData}
          color="#8B5CF6"
          yLabel=" kVA"
          lineType="stepAfter"
          refLines={[{ y: Math.round(contractKva), label: `${t("CD")} ${Math.round(contractKva)}`, stroke: '#EF4444' }]}
          xTicks={symmetricXTicks(mdRunMaxData, 5)}
        />
      )}
    </Card>
  );
}

/* ── 3b) Load Factor card ───────────────────────────────────────────────── */

const LF_REBATE_START = 65;

function LoadFactorBarChart({
  data,
  refLines = [],
  hasLfRebate = false,
  xTicks,
}: {
  data:          UtilTrendPoint[];
  refLines?:     { y: number; label: string; stroke: string }[];
  hasLfRebate?:  boolean;
  xTicks?:       string[];
}) {
  if (!data.length || data.every(d => d.value === 0)) {
    return <EmptyChart height={200} />;
  }

  const dataMax = Math.max(...data.map(d => d.value), 0);
  const refMax  = refLines.reduce((m, r) => Math.max(m, r.y), 0);
  const { max: yAxisMax, ticks: yAxisTicks } = niceYAxis(Math.max(dataMax, refMax), 5);

  const colorFor = hasLfRebate
    ? (v: number) =>
        v >= LF_REBATE_START ? '#10B981'
        : v >= 40             ? '#F59E0B'
        :                       '#EF4444'
    : (_v: number) => '#0EA5E9';

  return (
    <div style={{ width: '100%', height: 200 }}>
      <ResponsiveContainer>
        <BarChart data={data} margin={{ top: 18, right: 14, left: 8, bottom: 0 }} barCategoryGap="30%">
          <CartesianGrid stroke="#E2E8F0" vertical={false} />
          <XAxis dataKey="label" tickLine={false} axisLine={false}
            {...(xTicks ? { ticks: xTicks, interval: 0 } : { interval: 'preserveStartEnd', minTickGap: 28 })}
            tick={{ fontSize: 11, fontWeight: 700, fill: '#0F172A' }} />
          <YAxis tickLine={false} axisLine={false} width={40}
            domain={[0, yAxisMax]}
            ticks={yAxisTicks}
            tickFormatter={v => `${Math.round(v)}%`}
            tick={{ fontSize: 10, fontWeight: 700, fill: '#0F172A' }} />
          <Tooltip
            cursor={{ fill: 'rgba(0,0,0,0.04)' }}
            content={({ active, payload, label }) => {
              if (!active || !payload?.length) return null;
              const v = payload[0]?.value as number ?? 0;
              return (
                <div className="rounded-xl bg-white shadow-lg border border-slate-200 px-3 py-2 text-xs min-w-[120px]">
                  <div className="font-bold text-slate-900 mb-1">{label}</div>
                  <div className="num font-bold" style={{ color: colorFor(v) }}>
                    {v.toFixed(1)}%
                  </div>
                </div>
              );
            }}
          />
          {refLines.map(rl => (
            <ReferenceLine key={rl.label} y={rl.y} stroke={rl.stroke} strokeWidth={1.5}
              strokeDasharray="5 3"
              label={{ value: rl.label, position: 'insideTopRight', fontSize: 9, fontWeight: 700, fill: rl.stroke }} />
          ))}
          <Bar dataKey="value" radius={[4, 4, 0, 0]} maxBarSize={40}>
            {data.map((d, i) => <Cell key={i} fill={colorFor(d.value)} />)}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

function LoadFactorCard({ meterRows }: { meterRows: TelemetryRow[] }) {
  const { t } = useLanguage();
  const [win, setWin] = useState<UtilWindow>('today');
  const { hasLfRebate } = useTariff();
  const windowRows = useMemo(() => utilWindowRows(meterRows, win), [meterRows, win]);
  const lf         = loadFactor(windowRows);

  const todayLf = useMemo(() => loadFactor(utilWindowRows(meterRows, 'today')), [meterRows]);
  const lfWeekRows = useMemo(() => utilWindowRows(meterRows, 'thisWeek'), [meterRows]);
  const weekLf     = useMemo(() => loadFactor(lfWeekRows), [lfWeekRows]);

  const comparison = useMemo(() => {
    if (!hasLfRebate) return null;
    if (win === 'today') {
      if (!todayLf || !weekLf) return null;
      const diff = todayLf - weekLf;
      if (Math.abs(diff) < 0.5) return { label: t("Similar to this week's average"), tone: 'neutral' as const };
      return diff > 0
        ? { label: `${t("Today is")} +${diff.toFixed(1)}% ${t("above this week's avg — good day")}`, tone: 'good' as const }
        : { label: `${t("Today is")} ${diff.toFixed(1)}% ${t("below this week's avg — below average")}`, tone: 'warn' as const };
    }
    if (!todayLf || !lf) return null;
    const diff = todayLf - lf;
    if (Math.abs(diff) < 0.5) return { label: t("Similar to average"), tone: 'neutral' as const };
    return diff > 0
      ? { label: `${t("Today")} +${diff.toFixed(1)}% ${t("above")} ${t(utilWinLabel(win))} ${t("avg")}`, tone: 'good' as const }
      : { label: `${t("Today")} ${diff.toFixed(1)}% ${t("below")} ${t(utilWinLabel(win))} ${t("avg")}`, tone: 'warn' as const };
  }, [hasLfRebate, win, todayLf, lf, weekLf, t]);

  const trendData = useMemo(() => getLoadFactorTrend(windowRows, utilGranFor(win)), [windowRows, win]);

  const refLines = hasLfRebate
    ? win === 'today'
      ? [{ y: LF_REBATE_START, label: 'Rebate 65%', stroke: '#F59E0B' }, { y: 70, label: 'Better 70%', stroke: '#6366F1' }]
      : win === 'thisWeek' || win === 'thisMonth'
        ? [{ y: LF_REBATE_START, label: 'Rebate 65%', stroke: '#F59E0B' }]
        : []
    : [];

  // const gran        = utilGranFor(win);
  // Fixed x-axis ticks: 00:00 / 06:00 / 12:00 / 18:00 + last point for today;
  // for week/month use default preserveStartEnd behaviour.
  const lfXTicks    = symmetricXTicks(trendData, 5);

  const description = win === 'today'
    ? t("Total Energy (kWh) consumed today/ (Peak kW observed today times number of hours today)")
    : win === 'thisWeek'
      ? t("Total Energy (kWh) consumed this week / (Peak kW observed this week times number of hours this week)")
      : win === 'thisMonth'
        ? t("Total Energy (kWh) consumed this month / (Peak kW observed this month times number of hours this month)")
        : t("Total Energy (kWh) consumed last month / (Peak kW observed last month times number of hours last month)");

  return (
    <Card>
      <p className="text-[11px] font-bold tracking-wide uppercase text-ink-900 mb-0.5">{t("Load Factor Utilisation")}</p>
      <CardHeader
        title={
          <span className={cn('num', (win === 'today' && hasLfRebate) ? (lf >= LF_REBATE_START ? 'text-emerald-600' : lf >= 45 ? 'text-amber-500' : 'text-red-500') : 'text-ink-900')}>
            {formatPct(lf)}
          </span>
        }
        description={description}
      />
      <div className="mt-1 mb-2">
        <SegmentedToggle size="sm" value={win} onChange={v => setWin(v as UtilWindow)} options={UTIL_WIN_OPTS} ariaLabel="Load factor window" />
      </div>
      {win === 'today' && comparison && (
        <p className="text-xs font-bold text-slate-900 mb-2">
          {comparison.tone === 'good' ? '↑ ' : comparison.tone === 'warn' ? '↓ ' : '≈ '}
          {comparison.label}
        </p>
      )}
      {/* Bar chart for all windows — one bar per hour (today) or per day (week/month) */}
      <LoadFactorBarChart data={trendData} refLines={refLines} hasLfRebate={hasLfRebate} xTicks={lfXTicks} />
      {hasLfRebate && (
        <div className="flex items-center gap-3 mt-2 pt-2 border-t border-ink-100 flex-wrap">
          <span className="flex items-center gap-1.5">
            <span className="w-2.5 h-2.5 rounded-sm bg-red-500 shrink-0" />
            <span className="text-[10px] text-ink-500">{t("Penalty zone")}</span>
          </span>
          <span className="flex items-center gap-1.5">
            <span className="w-2.5 h-2.5 rounded-sm bg-amber-400 shrink-0" />
            <span className="text-[10px] text-ink-500">{t("No adjustment")}</span>
          </span>
          <span className="flex items-center gap-1.5">
            <span className="w-2.5 h-2.5 rounded-sm bg-emerald-500 shrink-0" />
            <span className="text-[10px] text-ink-500">{t("Rebate zone")}</span>
          </span>
        </div>
      )}
    </Card>
  );
}

/* -------------------------------------------------------------------------- */
/*  4) Energy by Time-of-Day                                                  */
/* -------------------------------------------------------------------------- */

const TOD_STACK_COLORS_MAP = {
  'OFF-PEAK': '#22D3EE',
  'NORMAL':   '#FBBF24',
  'PEAK':     '#F472B6',
} as const;

function bucketGranForPeriod(period: EnergyPeriod, customRange: CustomRange): Granularity {
  if (period === 'today') return 'hourly';
  if (period === 'thisWeek' || period === 'thisMonth') return 'daily';
  const fromTs = customRange.from ? new Date(`${customRange.from}T00:00:00`).getTime() : null;
  const toTs   = customRange.to   ? new Date(`${customRange.to}T23:59:59`).getTime()   : null;
  if (!fromTs || !toTs || toTs <= fromTs) return 'daily';
  const days = (toTs - fromTs) / (24 * 3_600_000);
  if (days <= 2)   return 'hourly';
  if (days <= 120) return 'daily';
  return 'monthly';
}

/* -------------------------------------------------------------------------- */
/*  PF Histogram — self-contained component used only inside PenaltyCard      */
/* -------------------------------------------------------------------------- */

function XTickHist({ x, y, payload }: { x?: number; y?: number; payload?: { value: string } }) {
  if (x == null || y == null) return null;
  return (
    <text x={x} y={y + 10} textAnchor="middle" fontSize={11} fontWeight="700" fill="#0F172A" fontFamily="inherit">
      {payload?.value}
    </text>
  );
}

function YTickHist({ x, y, payload }: { x?: number; y?: number; payload?: { value: number } }) {
  if (x == null || y == null) return null;
  const v = payload?.value ?? 0;
  const label = `${v.toFixed(1)}h`;
  return (
    <text x={x} y={y + 4} textAnchor="end" fontSize={11} fontWeight="700" fill="#0F172A" fontFamily="inherit">
      {label}
    </text>
  );
}

function PFHistogram({ rows }: { rows: TelemetryRow[] }) {
  const histogram = useMemo(() => getPFHistogram(rows), [rows]);

  const totalSamples = histogram.reduce((s, b) => s + b.count, 0);
  if (totalSamples === 0) return null;

  const maxHours = Math.max(...histogram.map((b) => b.hours), 0.1);
  const yHigh    = Number((maxHours * 1.25).toFixed(2));
  const showBrush = histogram.length > 10;
  const chartH    = showBrush ? 140 + 40 : 140;

  return (
    <div className="mt-3 mb-3">
      <p className="text-[11px] font-bold tracking-wide uppercase text-ink-900 mb-2">
        PF distribution · time in zone
      </p>

      <div style={{ width: '100%', height: chartH }}>
        <ResponsiveContainer>
          <BarChart
            data={histogram}
            margin={{ top: 4, right: 4, left: 12, bottom: 0 }}
            barCategoryGap="28%"
          >
            <CartesianGrid stroke="#CBD5E1" vertical={false} />
            <XAxis dataKey="label" tickLine={false} axisLine={false} tick={<XTickHist />} />
            <YAxis tickLine={false} axisLine={false} width={52} domain={[0, yHigh]} tick={<YTickHist />} />
            <Tooltip
              cursor={{ fill: 'rgba(15,177,138,0.06)' }}
              content={({ active, payload }) => {
                if (!active || !payload?.length) return null;
                const bin  = payload[0]?.payload;
                const hrs  = (bin?.hours as number) ?? 0;
                const mins = Math.round(hrs * 60);
                return (
                  <div className="rounded-xl bg-white shadow-lg border border-slate-200 px-3 py-2 text-xs min-w-[150px]">
                    <div className="font-bold text-slate-900 mb-1.5">PF {bin?.label}</div>
                    <div className="flex justify-between gap-4 mb-0.5">
                      <span className="font-semibold text-slate-600">Duration</span>
                      <span className="num font-bold text-slate-900">{hrs.toFixed(2)} h · {mins} min</span>
                    </div>
                    <div className="flex justify-between gap-4">
                      <span className="font-semibold text-slate-600">Instances</span>
                      <span className="num font-bold text-slate-900">{bin?.count} samples</span>
                    </div>
                  </div>
                );
              }}
            />
            <Bar dataKey="hours" radius={[4, 4, 0, 0]} maxBarSize={52}>
              {histogram.map((bin) => (
                <Cell key={bin.label} fill={bin.fill} />
              ))}
            </Bar>
            {showBrush && (
              <Brush
                dataKey="label"
                height={28}
                stroke="#CBD5E1"
                fill="#F1F5F9"
                travellerWidth={12}
              />
            )}
          </BarChart>
        </ResponsiveContainer>
      </div>

      <div className="flex items-center gap-4 mt-2 text-[11px] font-semibold text-ink-900">
        <span className="flex items-center gap-1">
          <span className="inline-block w-2.5 h-2.5 rounded-sm bg-[#EF4444]" />
          Critical &lt;0.85
        </span>
        <span className="flex items-center gap-1">
          <span className="inline-block w-2.5 h-2.5 rounded-sm bg-[#F59E0B]" />
          Warning 0.85–0.90
        </span>
        <span className="flex items-center gap-1">
          <span className="inline-block w-2.5 h-2.5 rounded-sm bg-[#0FB18A]" />
          Good ≥0.90
        </span>
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*  Shared helpers                                                            */
/* -------------------------------------------------------------------------- */

function defaultCustomRange(rows: TelemetryRow[]): CustomRange {
  if (!rows.length) return { from: '', to: '' };
  const latest = rows[rows.length - 1].timestamp;
  const start  = new Date(latest.getTime() - 6 * 24 * 3_600_000);
  return { from: toDateInput(start), to: toDateInput(latest) };
}

function toDateInput(d: Date): string {
  const y   = d.getFullYear();
  const m   = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function RangeSelector({ value, onChange }: { value: CustomRange; onChange: (next: CustomRange) => void }) {
  const MAX_MS = 92 * 24 * 3_600_000;

  const handleFrom = (from: string) => {
    const next = { ...value, from };
    if (next.from && next.to) {
      const fromTs = new Date(next.from).getTime();
      const toTs   = new Date(next.to).getTime();
      if (toTs - fromTs > MAX_MS) next.to = toDateInput(new Date(fromTs + MAX_MS));
    }
    onChange(next);
  };

  const handleTo = (to: string) => {
    const next = { ...value, to };
    if (next.from && next.to) {
      const fromTs = new Date(next.from).getTime();
      const toTs   = new Date(next.to).getTime();
      if (toTs - fromTs > MAX_MS) next.from = toDateInput(new Date(toTs - MAX_MS));
    }
    onChange(next);
  };

  return (
    <div className="mb-3 space-y-2">
      <div className="grid grid-cols-2 gap-2">
        <label className="text-[11px] font-medium text-ink-900">
          From
          <input
            type="date"
            value={value.from}
            onChange={(e) => handleFrom(e.target.value)}
            className="mt-1 w-full rounded-xl border border-ink-200 bg-white px-2 py-1.5 text-xs text-ink-900 outline-none focus:border-brand-400"
          />
        </label>
        <label className="text-[11px] font-medium text-ink-900">
          To
          <input
            type="date"
            value={value.to}
            onChange={(e) => handleTo(e.target.value)}
            className="mt-1 w-full rounded-xl border border-ink-200 bg-white px-2 py-1.5 text-xs text-ink-900 outline-none focus:border-brand-400"
          />
        </label>
      </div>
      <p className="text-[10px] text-ink-500 font-medium">Max view: 3 months</p>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*  UI primitives                                                             */
/* -------------------------------------------------------------------------- */

function MiniStat({
  label, value, accent,
}: { label: string; value: string; accent?: 'brand' | 'danger' }) {
  const { t } = useLanguage();
  const cls = accent === 'brand'  ? 'text-brand-600'
            : accent === 'danger' ? 'text-signal-danger'
            : 'text-ink-900';
  return (
    <div>
      <div className="text-[11px] font-bold tracking-wide uppercase text-ink-900">{t(label)}</div>
      <div className={`num text-sm font-semibold ${cls}`}>{value}</div>
    </div>
  );
}

interface StackedSegment { period: ToDPeriod; value: number; label: string; }

function ToDStackedBar({ segments }: { segments: StackedSegment[] }) {
  const { todColors: TOD_COLORS } = useTariff();
  const total = segments.reduce((a, s) => a + s.value, 0);
  if (total <= 0) return <div className="h-3 rounded-full bg-ink-100" />;
  return (
    <div className="flex h-3 w-full overflow-hidden rounded-full bg-ink-100">
      {segments.map((s) => {
        const w = (s.value / total) * 100;
        if (w <= 0) return null;
        return (
          <div
            key={s.period}
            title={`${s.period}: ${s.label} (${w.toFixed(2)}%)`}
            style={{ width: `${w}%`, backgroundColor: TOD_COLORS[s.period] }}
          />
        );
      })}
    </div>
  );
}

function PfZoneStrip({ pfAvg }: { pfAvg: number }) {
  const lo = 0.80, hi = 1.00;
  const clamped    = Math.min(hi, Math.max(lo, pfAvg || lo));
  const pct        = ((clamped - lo) / (hi - lo)) * 100;
  const penaltyEnd = ((PF_PENALTY_THRESHOLD - lo) / (hi - lo)) * 100;
  const neutralEnd = ((PF_REBATE_THRESHOLD  - lo) / (hi - lo)) * 100;

  return (
    <div className="relative">
      <div className="flex h-2.5 w-full overflow-hidden rounded-full">
        <div style={{ width: `${penaltyEnd}%` }} className="bg-signal-danger/70" />
        <div style={{ width: `${neutralEnd - penaltyEnd}%` }} className="bg-signal-warn/70" />
        <div style={{ width: `${100 - neutralEnd}%` }} className="bg-signal-good/70" />
      </div>
      {pfAvg > 0 && (
        <div
          className="absolute -top-1 w-1 bg-ink-900 rounded-full"
          style={{ left: `calc(${pct}% - 2px)`, height: 18 }}
          aria-label={`Average PF ${pfAvg.toFixed(3)}`}
        />
      )}
      <div className="flex justify-between mt-1 text-[10px] text-ink-900 num">
        <span>0.80</span>
        <span>{PF_PENALTY_THRESHOLD.toFixed(2)}</span>
        <span>{PF_REBATE_THRESHOLD.toFixed(2)}</span>
        <span>1.00</span>
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*  Exported billing-page variants                                            */
/* -------------------------------------------------------------------------- */

const BILLING_PERIOD_OPTS: { value: EnergyPeriod; label: string }[] = [
  { value: 'today',     label: 'Today'      },
  { value: 'thisWeek',  label: 'This Week'  },
  { value: 'thisMonth', label: 'This Month' },
  { value: 'custom',    label: 'Custom'     },
];

export function CostBreakdownBillingCard({ meterRows }: { meterRows: TelemetryRow[] }) {
  const { t } = useLanguage();
  const { todColors: TOD_COLORS, todWindows: TOD_WINDOWS } = useTariff();
  const [period, setPeriod] = useState<EnergyPeriod>('thisMonth');
  const [customRange, setCustomRange] = useState<CustomRange>(() => defaultCustomRange(meterRows));

  const windowRows = useMemo(() => {
    if (period === 'custom') {
      const from = customRange.from ? new Date(`${customRange.from}T00:00:00`) : undefined;
      const to   = customRange.to   ? new Date(`${customRange.to}T23:59:59.999`) : undefined;
      return energyWindowFor(meterRows, period, from && to ? { from, to } : undefined);
    }
    return energyWindowFor(meterRows, period);
  }, [meterRows, period, customRange]);

  const tod       = useMemo(() => costBreakdown(windowRows), [windowRows]);
  const totalCost = costRupees(windowRows);
  const totalKwh  = energyDelivered(windowRows);

  return (
    <Card>
      <p className="text-[11px] font-bold tracking-wide uppercase text-ink-900 mb-0.5">{t("Cost breakdown")}</p>
      <CardHeader
        title={formatRupees(totalCost, { compact: true })}
        description={`${formatKwh(totalKwh)} · ${t(energyPeriodLabel(period))}`}
      />
      <div className="mt-2 mb-3">
        <SegmentedToggle
          size="sm"
          value={period}
          onChange={setPeriod}
          options={BILLING_PERIOD_OPTS}
          ariaLabel="Cost breakdown period"
        />
      </div>
      {period === 'custom' && <RangeSelector value={customRange} onChange={setCustomRange} />}
      {windowRows.length === 0 ? (
        <EmptyChart height={200} />
      ) : (
        <>
          <ToDStackedBar segments={tod.map((t) => ({
            period: t.todPeriod, value: t.costRs, label: formatRupees(t.costRs, { compact: true }),
          }))} />
          {tod.length ? (
            <ul className="mt-3 space-y-1.5">
              {tod.map((tItem) => (
                <li key={tItem.todPeriod} className="flex items-center justify-between text-xs">
                  <span className="inline-flex items-center gap-1.5">
                    <span className="w-2 h-2 rounded-full" style={{ backgroundColor: TOD_COLORS[tItem.todPeriod] }} />
                    <span className="font-semibold text-ink-900">{t(TOD_WINDOWS[tItem.todPeriod].label)}</span>
                  </span>
                  <span className="num text-ink-900">
                    {formatRupees(tItem.costRs, { compact: true })} · {tItem.pctOfDay.toFixed(2)}%
                  </span>
                </li>
              ))}
            </ul>
          ) : null}
        </>
      )}
    </Card>
  );
}

export function EnergyByTodBillingCard({ meterRows }: { meterRows: TelemetryRow[] }) {
  const { t } = useLanguage();
  const tariff = useTariff();
  const { todWindows: TOD_WINDOWS } = tariff;
  const isKvah = tariff.energyUnit === 'kvah';
  const energyUnitLabel = isKvah ? 'kVAh' : 'kWh';

  const [period, setPeriod] = useState<EnergyPeriod>('thisMonth');
  const [customRange, setCustomRange] = useState<CustomRange>(() => defaultCustomRange(meterRows));

  const windowRows = useMemo(() => {
    if (period === 'custom') {
      const from = customRange.from ? new Date(`${customRange.from}T00:00:00`) : undefined;
      const to   = customRange.to   ? new Date(`${customRange.to}T23:59:59.999`) : undefined;
      return energyWindowFor(meterRows, period, from && to ? { from, to } : undefined);
    }
    return energyWindowFor(meterRows, period);
  }, [meterRows, period, customRange]);

  const gran     = bucketGranForPeriod(period, customRange);
  const buckets  = useMemo(() => energyByTodBuckets(windowRows, gran, isKvah), [windowRows, gran, isKvah]);
  const tod      = useMemo(() => energyByTodPeriod(windowRows, isKvah), [windowRows, isKvah]);
  const totalEnergy = isKvah
    ? (() => {
        if (windowRows.length < 2) return 0;
        const sorted = [...windowRows].sort((a, b) => a.timestamp.getTime() - b.timestamp.getTime());
        return Math.max(0, (sorted[sorted.length - 1].energyVAh ?? 0) - (sorted[0].energyVAh ?? 0)) / 1000;
      })()
    : energyDelivered(windowRows);

  return (
    <Card>
      <p className="text-[11px] font-bold tracking-wide uppercase text-ink-900 mb-0.5">{t("Energy by Time-of-Day")}</p>
      <CardHeader
        title={`${totalEnergy.toFixed(2)} ${energyUnitLabel}`}
        description={`${t("Off-Peak")} · ${t("Normal")} · ${t("Peak")} — ${t(energyPeriodLabel(period))}`}
      />
      <div className="mt-2 mb-3">
        <SegmentedToggle
          size="sm"
          value={period}
          onChange={setPeriod}
          options={BILLING_PERIOD_OPTS}
          ariaLabel="Energy by ToD period"
        />
      </div>
      {period === 'custom' && <RangeSelector value={customRange} onChange={setCustomRange} />}
      {windowRows.length === 0 ? (
        <EmptyChart height={200} />
      ) : (
        <>
          <TodEnergyBarChart data={buckets} useKvah={isKvah} />
          {tod.length ? (
            <ul className="mt-3 space-y-1.5">
              {tod.map((tItem) => (
                <li key={tItem.todPeriod} className="flex items-center justify-between text-xs">
                  <span className="inline-flex items-center gap-1.5 min-w-0">
                    <span className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: TOD_STACK_COLORS_MAP[tItem.todPeriod] }} />
                    <span className="font-semibold text-ink-900 truncate">{t(TOD_WINDOWS[tItem.todPeriod].label)}</span>
                    <span className="text-[10px] text-ink-900 shrink-0">{TOD_WINDOWS[tItem.todPeriod].range}</span>
                  </span>
                  <span className="num text-ink-900 shrink-0">
                    {tItem.kwh.toFixed(2)} {energyUnitLabel} · {tItem.pctOfDay.toFixed(2)}%
                  </span>
                </li>
              ))}
            </ul>
          ) : null}
        </>
      )}
    </Card>
  );
}

export function PFPenaltyBillingCard({ meterRows }: { meterRows: TelemetryRow[] }) {
  const { t } = useLanguage();
  const [gran, setGran] = useState<RangeMode>('daily');
  const windowRows = useMemo(
    () => windowFor(meterRows, gran as Granularity),
    [meterRows, gran],
  );
  const penalty = useMemo(() => evaluatePenalty(windowRows), [windowRows]);

  const headline = penalty.applies
    ? { tone: 'danger' as const, label: t('Penalty likely'),
        amount: `+${formatRupees(penalty.estimatedExtraRs, { compact: true })}` }
    : penalty.rebateApplies
    ? { tone: 'good'   as const, label: t('Rebate earned'),
        amount: `−${formatRupees(penalty.estimatedRebateRs, { compact: true })}` }
    : { tone: 'muted'  as const, label: t('No adjustment'), amount: '—' };

  const pillClass: Record<typeof headline.tone, string> = {
    good:   'bg-signal-good/10 text-signal-good',
    danger: 'bg-signal-danger/10 text-signal-danger',
    muted:  'bg-ink-100 text-ink-900',
  };

  return (
    <Card>
      <p className="text-[11px] font-bold tracking-wide uppercase text-ink-900 mb-0.5">{t("PF Penalty / Rebate")}</p>
      <CardHeader
        title={penalty.pfAvg ? formatPF(penalty.pfAvg) : '—'}
        description={`${t("Average power factor across")} ${t(windowLabel(gran as Granularity))}`}
      />
      <div className="mt-2 mb-3">
        <SegmentedToggle
          size="sm"
          value={gran}
          onChange={setGran}
          options={GRAN_OPTS}
          ariaLabel="PF Penalty period"
        />
      </div>

      <div className="flex items-center justify-between gap-2 mb-3">
        <span className={
          'inline-flex items-center gap-1 px-2 py-1 rounded-full text-[11px] font-semibold ' +
          pillClass[headline.tone]
        }>
          {headline.tone === 'danger' ? <ShieldAlert className="w-3 h-3" /> :
           headline.tone === 'good'   ? <Gift        className="w-3 h-3" /> :
                                        <Activity    className="w-3 h-3" />}
          {headline.label}
        </span>
        <span className={
          'num text-base font-semibold ' +
          (headline.tone === 'danger' ? 'text-signal-danger'
           : headline.tone === 'good'  ? 'text-signal-good'
           : 'text-ink-900')
        }>
          {headline.amount}
        </span>
      </div>

      <PfZoneStrip pfAvg={penalty.pfAvg} />

      <div className="mt-3 grid grid-cols-3 gap-2 text-center">
        <MiniStat label="Avg PF" value={penalty.pfAvg ? penalty.pfAvg.toFixed(3) : '—'} />
        <MiniStat
          label="Min PF"
          value={penalty.pfMin ? penalty.pfMin.toFixed(3) : '—'}
          accent={penalty.pfMin && penalty.pfMin < 0.85 ? 'danger' : undefined}
        />
        <MiniStat
          label="Adjustment"
          value={penalty.adjustmentPct ? `${penalty.adjustmentPct.toFixed(2)}%` : '—'}
          accent={penalty.applies ? 'danger' : penalty.rebateApplies ? 'brand' : undefined}
        />
      </div>

      {windowRows.length === 0 ? (
        <EmptyChart height={200} />
      ) : (
        <PFHistogram rows={windowRows} />
      )}
    </Card>
  );
}

/* -------------------------------------------------------------------------- */
/*  Unified PF Penalty + Adjustment card — used in Billings page             */
/* -------------------------------------------------------------------------- */

interface PFPenaltyAndAdjustmentCardProps {
  meterRows:    TelemetryRow[];
  pfAvg:        number;
  pfRebatePct:  number;
  pfRebateCost: number;
  activePfSlab: number;
}

export function PFPenaltyAndAdjustmentCard({
  meterRows: _meterRows,
  pfAvg,
  pfRebatePct,
  pfRebateCost,
  activePfSlab,
}: PFPenaltyAndAdjustmentCardProps) {
  const { t } = useLanguage();
  const { pfAdjustmentTable: PF_ADJUSTMENT_TABLE } = useTariff();
  const mtdColor = pfRebateCost > 0 ? 'text-emerald-600' : pfRebateCost < 0 ? 'text-red-600' : 'text-ink-500';
  const mtdAmt   = formatRupees(Math.abs(pfRebateCost));

  const pointerPct = Math.min(99, Math.max(0, pfAvg * 100));

  const rebatePctColor = pfRebatePct > 0 ? 'text-emerald-600' : pfRebatePct < 0 ? 'text-red-600' : 'text-ink-500';
  const rebateLabel    = pfRebatePct > 0 ? t('Rebate') : pfRebatePct < 0 ? t('Penalty') : t('No adj.');

  return (
    <Card padded>
      <CardHeader
        title="PF Rebate / Penalty"
        trailing={
          <span className={cn('num text-sm font-bold', mtdColor)}>
            {pfRebateCost > 0 ? '+' : pfRebateCost < 0 ? '−' : ''}{mtdAmt}
          </span>
        }
        description="WBSEDCL §3.2 — applied as % of energy charge per ToD period"
      />

      <div className="mb-4 mt-4 p-3 rounded-xl bg-ink-50 flex items-center gap-4">
        <div className="flex flex-col items-center shrink-0">
          <span className={cn(
            'num text-2xl font-bold',
            pfAvg >= 0.99 ? 'text-emerald-600'
            : pfAvg >= 0.95 ? 'text-brand-600'
            : pfAvg < 0.80 ? 'text-red-600'
            : 'text-ink-900',
          )}>
            {pfAvg > 0 ? formatPF(pfAvg) : '—'}
          </span>
          <span className="text-[9px] text-ink-400 uppercase tracking-wide mt-0.5">Avg PF</span>
        </div>

        <div className="flex-1 h-3 bg-ink-200 rounded-full overflow-hidden relative">
          <div className="absolute inset-0 flex">
            <div className="h-full bg-red-300"      style={{ width: '80%' }} />
            <div className="h-full bg-amber-200"    style={{ width: '15%' }} />
            <div className="h-full bg-emerald-300"  style={{ width: '5%'  }} />
          </div>
          {pfAvg > 0 && (
            <div
              className="absolute top-0 w-1 h-full bg-ink-900 rounded-full shadow-md"
              style={{ left: `${pointerPct}%` }}
            />
          )}
        </div>

        <div className="flex flex-col items-end shrink-0">
          <span className={cn('num text-base font-bold', rebatePctColor)}>
            {pfRebatePct > 0 ? '+' : ''}{pfRebatePct}%
          </span>
          <span className="text-[9px] text-ink-400 uppercase tracking-wide mt-0.5">
            {rebateLabel}
          </span>
        </div>
      </div>

      <div className="space-y-0.5">
        <div className="grid grid-cols-4 gap-1 px-2 pb-1">
          {(['PF Range', 'Normal', 'Peak', 'Off-Peak'] as const).map(h => (
            <span
              key={h}
              className="text-[9px] font-bold uppercase tracking-wider text-ink-400 text-center first:text-left"
            >
              {t(h)}
            </span>
          ))}
        </div>

        {PF_ADJUSTMENT_TABLE.map((row: PfAdjustmentRow, i: number) => {
          const active   = i === activePfSlab;
          const pfLabel  = row.maxPf === Infinity
            ? `≥ ${row.minPf.toFixed(2)}`
            : `${row.minPf.toFixed(2)} – ${row.maxPf.toFixed(2)}`;
          const colourOf = (pct: number) => pct > 0 ? 'text-emerald-600' : pct < 0 ? 'text-red-600' : 'text-ink-400';
          return (
            <div
              key={i}
              className={cn(
                'grid grid-cols-4 gap-1 text-[10px] py-1.5 px-2 rounded-lg',
                active ? 'bg-brand-50 ring-1 ring-brand-200' : 'odd:bg-ink-50/50',
              )}
            >
              <span className={cn('font-mono', active && 'font-bold text-brand-700')}>{pfLabel}</span>
              <span className={cn('text-center', colourOf(row.normalPct))}>
                {row.normalPct > 0 ? '+' : ''}{row.normalPct}%
              </span>
              <span className={cn('text-center', colourOf(row.peakPct))}>
                {row.peakPct > 0 ? '+' : ''}{row.peakPct}%
              </span>
              <span className={cn('text-center', colourOf(row.offPeakPct))}>
                {row.offPeakPct > 0 ? '+' : ''}{row.offPeakPct}%
              </span>
            </div>
          );
        })}
      </div>

      <div className="mt-3 pt-3 border-t border-ink-100">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-bold text-ink-900">
              {t("PF rebate / penalty MTD")}
            </p>
            <p className="text-[10px] text-ink-400 mt-0.5">
              {pfRebateCost !== 0 ? (pfRebateCost > 0 ? t('added to bill') : t('deducted from bill')) : t('no adjustment')}
            </p>
          </div>
          <span className={cn('num text-sm font-bold', mtdColor)}>
            {pfRebateCost > 0 ? '+' : pfRebateCost < 0 ? '−' : ''}{mtdAmt}
          </span>
        </div>
      </div>
    </Card>
  );
}